import { useEffect } from 'react'

export interface DocumentMeta {
  title: string
  description?: string | null
  image?: string | null
  url?: string | null
}

const DEFAULT_TITLE = 'JustLoot.me - Free games and in-game item giveaways'

function setTag(selector: string, attribute: 'name' | 'property', key: string, value: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', value)
}

/**
 * Keeps the document title and Open Graph tags in step with the giveaway on
 * screen, so a link shared out of the app carries the right name, blurb and
 * art — and so the browser tab and history entry read sensibly.
 *
 * Crawlers that do not run JavaScript (Discord's unfurler among them) never
 * see these; `server/index.mjs` injects the same tags into the HTML it serves
 * for `/g/:slug`. This hook covers the in-app case, that server covers the
 * shared-link case, and both read from the same shape.
 */
export function useDocumentMeta(meta: DocumentMeta | null) {
  useEffect(() => {
    if (!meta) return

    const previousTitle = document.title
    document.title = meta.title === DEFAULT_TITLE ? meta.title : `${meta.title} · JustLoot.me`

    setTag('meta[property="og:title"]', 'property', 'og:title', meta.title)
    setTag('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title)
    setTag('meta[property="og:type"]', 'property', 'og:type', 'website')
    setTag(
      'meta[name="twitter:card"]',
      'name',
      'twitter:card',
      meta.image ? 'summary_large_image' : 'summary',
    )

    if (meta.description) {
      setTag('meta[name="description"]', 'name', 'description', meta.description)
      setTag('meta[property="og:description"]', 'property', 'og:description', meta.description)
      setTag('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description)
    }
    if (meta.image) {
      setTag('meta[property="og:image"]', 'property', 'og:image', meta.image)
      setTag('meta[name="twitter:image"]', 'name', 'twitter:image', meta.image)
    }
    if (meta.url) {
      setTag('meta[property="og:url"]', 'property', 'og:url', meta.url)

      let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.rel = 'canonical'
        document.head.appendChild(canonical)
      }
      canonical.href = meta.url
    }

    return () => {
      document.title = previousTitle
    }
  }, [meta])
}
